import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

const SSEContext = createContext();

const ALLOWED_ROLES = ['BAAS', 'FLOORMANAGER', 'CHEFKOK', 'SOUSCHEF', 'HORECAMASTERS'];

export const SSEProvider = ({ children }) => {
    const [reservations, setReservations] = useState([]);
    const intervalIdRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [token, setToken] = useState(sessionStorage.getItem('authToken'));

    // Function to check if the token has any of the allowed roles
    const hasAllowedRole = (token) => {
        if (!token) return false;
        try {
            const decoded = jwtDecode(token);
            if (decoded.roles && Array.isArray(decoded.roles)) {
                return decoded.roles.some(role => ALLOWED_ROLES.includes(role));
            }
            if (decoded.role) {
                return ALLOWED_ROLES.includes(decoded.role);
            }
            return false;
        } catch (error) {
            console.error('Error decoding token:', error);
            return false;
        }
    };

    useEffect(() => {
        const storedToken = sessionStorage.getItem('authToken');
        if (storedToken && hasAllowedRole(storedToken)) {
            setToken(storedToken);
            startFetch(storedToken);
            setIsConnected(true);
        }

        intervalIdRef.current = setInterval(() => {
            const newToken = sessionStorage.getItem('authToken');
            if (newToken && newToken !== token && hasAllowedRole(newToken)) {
                setToken(newToken);
                startFetch(newToken);
                setIsConnected(true);
            }
        }, 60000); // Refresh token every minute

        return () => clearInterval(intervalIdRef.current);
    }, [token, isConnected]);

    const startFetch = (token) => {
        //const url = 'http://localhost:4000/reservations/stream';
         const url = 'https://api.horeca-masters.nl/reservations/stream';
        fetchSSE(url, token, handleMessage, handleError);
    };

    const handleMessage = (data) => {
        try {
            if (data.startsWith('event:')) {
                const [eventLine, ...dataLines] = data.split('\n');
                const eventType = eventLine.split(':')[1]?.trim();
                data = dataLines.join('\n').replace('data:', '').trim();

                if (eventType === 'initial-data') {
                    const dataArray = JSON.parse(data);

                    if (Array.isArray(dataArray)) {
                        const formattedEvents = dataArray.map((reservation) => {
                            const customer = reservation.customer || { firstName: 'Unknown', lastName: 'Customer' };
                            const reservationDateTime = new Date(`${reservation.reservationDate}T${reservation.reservationTime}`);
                            const formattedTime = reservationDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                            return {
                                id: reservation.reservationId,
                                title: `${formattedTime} ${customer.firstName} ${customer.lastName}`,
                                start: reservationDateTime,
                                end: reservationDateTime,
                                data: reservation,
                                allDay: false,
                            };
                        });

                        setReservations(formattedEvents);
                    } else {
                        console.warn('Expected an array but got:', dataArray);
                    }
                } else if (eventType === 'reservation-update') {
                    const updatedData = JSON.parse(data);

                    if (Array.isArray(updatedData)) {
                        const formattedUpdates = updatedData.map((reservation) => {
                            const customer = reservation.customer || { firstName: 'Unknown', lastName: 'Customer' };
                            const reservationDateTime = new Date(`${reservation.reservationDate}T${reservation.reservationTime}`);
                            const formattedTime = reservationDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                            return {
                                id: reservation.reservationId,
                                title: `${formattedTime} ${customer.firstName} ${customer.lastName}`,
                                start: reservationDateTime,
                                end: reservationDateTime,
                                data: reservation,
                                allDay: false,
                            };
                        });

                        setReservations((prevReservations) => {
                            const existingMap = new Map(prevReservations.map((r) => [r.id, r]));
                            formattedUpdates.forEach((update) => {
                                existingMap.set(update.id, update);
                            });
                            return Array.from(existingMap.values());
                        });
                    } else {
                        console.warn('Expected an array but got:', updatedData);
                    }
                } else {
                    console.warn(`Unhandled event type: ${eventType}`);
                }
            } else {
                console.warn('Unrecognized message format:', data);
            }
        } catch (error) {
            console.error('Error processing SSE data:', error);
        }
    };

    const handleError = (error) => {
        console.error('Error with SSE:', error);
    };

    const fetchSSE = async (url, token, onMessage, onError) => {
        try {
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                let boundary = buffer.indexOf('\n\n');
                while (boundary !== -1) {
                    const data = buffer.slice(0, boundary).trim();
                    if (data) {
                        onMessage(data);
                    }
                    buffer = buffer.slice(boundary + 2);
                    boundary = buffer.indexOf('\n\n');
                }
            }

            if (buffer.length > 0) {
                onMessage(buffer);
            }
        } catch (error) {
            onError(error);
        }
    };

    return (
        <SSEContext.Provider value={{ reservations }}>
            {children}
        </SSEContext.Provider>
    );
};

export const useSSE = () => {
    return useContext(SSEContext);
};
